import { useState, type ReactNode } from "react";
import { isSuperadminRequired, useServiceAdmin, type ServiceAdminSlug } from "./use-service-admin.ts";
import { SuperadminOnly } from "./SuperadminOnly.tsx";
import { DetailDrawer, DrawerSection, KeyValue } from "../../agentdna/DetailDrawer.tsx";
import { isoAttr } from "../../../lib/datetime.ts";

/**
 * The shared admin transaction-log viewer for IDP and AIS (both expose the identical
 * `/admin/api/logs` shape). Migrated from the services' server-rendered log pages into the control
 * plane. The list columns mirror the original admin UI — Timestamp, Event, Method, Path, Status —
 * with a "N matching" count; clicking a ROW opens the full detail in the right-side fly-out
 * {@link DetailDrawer} (the same pattern as the Interactions screen), showing the entry's id,
 * timestamp, duration, method, path, status, event, and the captured request/response headers.
 *
 * The log entry shape is the services' redacted transaction-log record: `request`/`response` are
 * nested objects, so method/path come from `request` and status from `response`.
 */

interface HeaderMap {
  readonly [k: string]: string;
}
interface LogEntry {
  readonly id: number;
  readonly timestamp: string;
  readonly duration?: number | null;
  readonly request?: {
    readonly method?: string;
    readonly path?: string;
    readonly headers?: HeaderMap;
    readonly query?: Record<string, unknown>;
    readonly body?: unknown;
  } | null;
  readonly response?: {
    readonly status?: number;
    readonly headers?: HeaderMap;
    readonly body?: unknown;
  } | null;
  readonly event?: { readonly type?: string; readonly metadata?: Record<string, unknown> } | null;
}

export function LogsTable(props: { slug: ServiceAdminSlug }): ReactNode {
  const [selected, setSelected] = useState<LogEntry | null>(null);
  const query = useServiceAdmin<readonly LogEntry[]>(props.slug, "logs?limit=200", ["logs"]);
  const entries = query.data ?? [];
  const titleId = "svc-log-detail-title";

  if (query.isError) {
    if (isSuperadminRequired(query.error)) return <SuperadminOnly what="The transaction log" />;
    return (
      <div className="state-box" data-kind="error" role="alert">
        <strong>Could not load logs</strong>
        <span>The {props.slug.toUpperCase()} admin service could not be reached. {query.error.message}</span>
      </div>
    );
  }

  return (
    <section className="svc-logs" aria-label="Transaction logs">
      <div className="svc-logs__head">
        <h3 className="svc-admin__h">Transaction logs</h3>
        {!query.isPending && <span className="svc-logs__count">{entries.length} matching</span>}
      </div>

      {query.isPending ? (
        <div className="state-box" data-kind="loading" role="status"><strong>Loading…</strong><span>Fetching logs.</span></div>
      ) : entries.length === 0 ? (
        <div className="state-box" data-kind="empty"><strong>No log entries</strong><span>No activity has been recorded yet.</span></div>
      ) : (
        <table className="svc-logs__table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Event</th>
              <th>Method</th>
              <th>Path</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr
                key={e.id}
                className="svc-logs__row"
                tabIndex={0}
                role="button"
                aria-label={`View details for ${e.request?.method ?? ""} ${e.request?.path ?? ""} at ${e.timestamp}`}
                onClick={() => setSelected(e)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") {
                    ev.preventDefault();
                    setSelected(e);
                  }
                }}
              >
                <td className="svc-logs__ts">
                  <time dateTime={isoAttr(Date.parse(e.timestamp))}>{e.timestamp}</time>
                </td>
                <td className="svc-logs__event">{e.event?.type ?? "—"}</td>
                <td className="svc-logs__method">{e.request?.method ?? "—"}</td>
                <td className="svc-logs__path">{e.request?.path ?? "—"}</td>
                <td className="svc-logs__status" data-status={statusClass(e.response?.status)}>
                  {e.response?.status ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <DetailDrawer open={selected !== null} onClose={() => setSelected(null)} labelledBy={titleId}>
        {selected !== null && <LogDetail entry={selected} titleId={titleId} onClose={() => setSelected(null)} />}
      </DetailDrawer>
    </section>
  );
}

function LogDetail(props: { entry: LogEntry; titleId: string; onClose: () => void }): ReactNode {
  const { entry } = props;
  const reqHeaders = entry.request?.headers ?? {};
  const resHeaders = entry.response?.headers ?? {};

  return (
    <>
      <header className="drawer-head">
        <div className="drawer-head__title">
          <h2 id={props.titleId} className="drawer-head__heading">Log entry #{entry.id}</h2>
          <span className="drawer-head__id">{entry.request?.method ?? ""} {entry.request?.path ?? ""}</span>
        </div>
        <button type="button" className="drawer-head__close" onClick={props.onClose} aria-label="Close details">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      <div className="drawer-body">
        <DrawerSection title="Overview">
          <KeyValue
            rows={[
              { k: "ID", v: <code className="drawer-mono">{entry.id}</code> },
              { k: "Timestamp", v: entry.timestamp },
              { k: "Duration", v: entry.duration != null ? `${entry.duration} ms` : "—" },
              { k: "Method", v: entry.request?.method ?? "—" },
              { k: "Path", v: <code className="drawer-mono">{entry.request?.path ?? "—"}</code> },
              { k: "Status", v: entry.response?.status ?? "—" },
              ...(entry.event?.type ? [{ k: "Event", v: entry.event.type }] : []),
            ]}
          />
        </DrawerSection>

        {entry.event?.metadata && Object.keys(entry.event.metadata).length > 0 && (
          <DrawerSection title="Event metadata">
            <KeyValue rows={Object.entries(entry.event.metadata).map(([k, v]) => ({ k, v: stringify(v) }))} />
          </DrawerSection>
        )}

        {Object.keys(reqHeaders).length > 0 && (
          <DrawerSection title="Request headers">
            <pre className="svc-logs__headers">{Object.entries(reqHeaders).map(([k, v]) => `${k}: ${v}`).join("\n")}</pre>
          </DrawerSection>
        )}

        {Object.keys(resHeaders).length > 0 && (
          <DrawerSection title="Response headers">
            <pre className="svc-logs__headers">{Object.entries(resHeaders).map(([k, v]) => `${k}: ${v}`).join("\n")}</pre>
          </DrawerSection>
        )}

        {entry.request?.query && Object.keys(entry.request.query).length > 0 && (
          <DrawerSection title="Query Parameters">
            <KeyValue
              rows={Object.entries(entry.request.query).map(([k, v]) => ({ k, v: stringify(v) }))}
            />
          </DrawerSection>
        )}

        {hasBody(entry.request?.body) && (
          <DrawerSection title="Request Body">
            <pre className="svc-logs__headers">{formatBody(entry.request?.body)}</pre>
          </DrawerSection>
        )}

        {hasBody(entry.response?.body) && (
          <DrawerSection title="Response Body (truncated)">
            <pre className="svc-logs__headers">{truncate(formatBody(entry.response?.body))}</pre>
          </DrawerSection>
        )}
      </div>
    </>
  );
}

function statusClass(status: number | undefined): "ok" | "warn" | "error" | "none" {
  if (status === undefined) return "none";
  if (status >= 500) return "error";
  if (status >= 400) return "warn";
  return "ok";
}

function stringify(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/**
 * True when a body value is worth rendering: a non-null/undefined value and, for strings, a
 * non-empty one. An empty-string body must NOT force an empty section (Fixes 2.3-2.4).
 *
 * Exported so the flyout render (task 4.2) and the body/section unit tests (task 10) share the
 * exact same predicate.
 */
export function hasBody(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.length > 0;
  return true;
}

/** Render a body readably: strings as-is, everything else pretty-printed JSON (Fixes 2.3-2.4). */
export function formatBody(v: unknown): string {
  return typeof v === "string" ? v : JSON.stringify(v, null, 2);
}

/** Max characters of Response Body rendered before truncation (Fix 2.4). */
const BODY_CAP = 2000;

/** Return `s` unchanged within {@link BODY_CAP}, otherwise the first cap chars plus an ellipsis. */
export function truncate(s: string): string {
  return s.length <= BODY_CAP ? s : `${s.slice(0, BODY_CAP)}…`;
}
